'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var mustache = require('mustache');
var clsx = require('clsx');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthBitHeader = require('@narvar/nth-bit-header');
var Paragraph = require('@narvar/nth-bit-paragraph');
var TextLink = require('@narvar/nth-bit-text-link');
var nthHookContextPage = require('@narvar/nth-hook-context-page');
var Button = require('@narvar/nth-block-button');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var nthHookSettings = require('@narvar/nth-hook-settings');
var nthHookGlobalSettings = require('@narvar/nth-hook-global-settings');
var layoutTree = require('@narvar/nth-kit-dynamic-layout-next/dist/src/layoutTree');
var useOrderTracking = require('@narvar/nth-hook-order-tracking');
var ItemImage = require('./ItemImage.js');
var nthPackItemsInThisShipment = require('@narvar/nth-pack-items-in-this-shipment');
var TrackingStatusText = require('./TrackingStatusText.js');
var ShipmentRow_module = require('./ShipmentRow.module.css.js');
var ProductThumbnail = require('@narvar/nth-block-product-thumbnail');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var mustache__default = /*#__PURE__*/_interopDefaultLegacy(mustache);
var Paragraph__default = /*#__PURE__*/_interopDefaultLegacy(Paragraph);
var TextLink__default = /*#__PURE__*/_interopDefaultLegacy(TextLink);
var Button__default = /*#__PURE__*/_interopDefaultLegacy(Button);
var useOrderTracking__default = /*#__PURE__*/_interopDefaultLegacy(useOrderTracking);

var DateTokenType = /*#__PURE__*/function (DateTokenType) {
  DateTokenType["DAY_OF_MONTH"] = "DAY_OF_MONTH";
  DateTokenType["DAY_OF_WEEK"] = "DAY_OF_WEEK";
  DateTokenType["MONTH"] = "MONTH";
  return DateTokenType;
}(DateTokenType || {});
const renderOptions = {
  escape: x => x
};
const ShipmentRow = ({
  landscape,
  featureName,
  index,
  shipment,
  trackingType,
  carouselScrolls = 0
}) => {
  const logEvent = nthHookContextAnalytics.useAnalyticsPost();
  const {
    state
  } = nthHookContextPage.usePageContext();
  const {
    setCurrentShipment
  } = useOrderTracking__default["default"]();
  const {
    category,
    mainShipmentId
  } = state;
  const [showItemDetails, setShowItemDetails] = React.useState(false);
  const {
    status,
    items,
    link,
    displayDateFormatted,
    statusCode,
    shipmentId
  } = shipment;

  // The border of each shipment should match the styles from panelcontainerNoneNone
  // The padding of each shipment should match the padding of the panelbodyNoneNone,
  // since `Shipments` sets that padding to 0 so that the bottom border of each shipment
  // can extend to the edge of the panel container. So we want each shipment to inherit
  // that padding, instead of having it defined on the inner container of panel
  const {
    panelcontainerNoneNone: {
      borderTopColor,
      borderTopStyle,
      borderTopWidth
    } = {
      borderTopColor: undefined,
      borderTopStyle: undefined,
      borderTopWidth: undefined
    },
    linksNoneDefault,
    linksNoneHover
  } = nthHookTheme.useTheme();
  const {
    shipmentsItemLabel,
    shipmentsButton,
    packShipmentsDateTokenOrder,
    packShipmentsMoreDetails,
    packShipmentsHideDetails,
    itemsInShipmentItemTitle,
    itemsInShipmentAttribute0,
    itemsInShipmentAttribute1,
    itemsInShipmentAttribute2,
    itemsInShipmentAttribute3
  } = nthHookTranslations.useTranslations();
  const itemTextTemplates = {
    itemsInShipmentItemTitle,
    itemsInShipmentAttribute0,
    itemsInShipmentAttribute1,
    itemsInShipmentAttribute2,
    itemsInShipmentAttribute3
  };
  const {
    loading,
    settings
  } = nthHookSettings.useSettingsX();
  const {
    globalSettings: {
      internationalDateFormatting,
      giftSharingEnabled
    }
  } = nthHookGlobalSettings.useGlobalSettingsX();
  const updatePageMainShipment = () => {
    if (!shipmentId) return;
    setCurrentShipment(shipmentId);
    // after updating page shipment info, reset page focus for a11y.
    if (typeof window === 'object') {
      window.scroll({
        top: 0
      });
      document.body.focus();
    }
  };

  // hide item details whenever the shipments carousel scrolls
  React.useEffect(() => {
    setShowItemDetails(false);
  }, [carouselScrolls]);
  if (loading) return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null);
  const [dayOfWeek, month, dayOfMonth] = displayDateFormatted;
  const displayDateWithTokens = {
    [DateTokenType.DAY_OF_WEEK]: dayOfWeek,
    [DateTokenType.MONTH]: month,
    [DateTokenType.DAY_OF_MONTH]: dayOfMonth
  };
  const formattedOrderedDate = internationalDateFormatting && typeof packShipmentsDateTokenOrder === 'string' ? packShipmentsDateTokenOrder.split(',').map(dateToken => {
    if (dateToken in DateTokenType) return displayDateWithTokens[dateToken];
    return null;
  }) : displayDateFormatted;
  const paragraphStyles = {
    marginTop: 0,
    marginBottom: '.5rem',
    lineHeight: '1.25rem'
  };
  const {
    imageUrl: imageUrlTemplate,
    url: urlTemplate = ''
  } = settings.shipments ?? {};
  const {
    totalItemsQuantity,
    itemImages
  } = (items || []).reduce((accum, item) => {
    if (!item) return accum;

    // Currently the "hack" to not show the product images is setting the
    // image template setting to ' '. In an upcoming refresh of the shipments
    // component settings, there will be a boolean flag
    // we'll be able to check instead of looking for ' '.
    const hideProductImage = imageUrlTemplate === ' ';
    const itemImage = hideProductImage || ProductThumbnail.imageWouldRenderEmpty(item, imageUrlTemplate) ? null : /*#__PURE__*/React__default["default"].createElement(ItemImage["default"], {
      featureName: featureName,
      item: item,
      key: item.name || item.imageUrl || item.url || '',
      urlTemplate: urlTemplate,
      imageUrlTemplate: imageUrlTemplate
    });
    return {
      totalItemsQuantity: accum.totalItemsQuantity + (item.quantity || 0),
      itemImages: itemImage ? [...accum.itemImages, itemImage] : accum.itemImages
    };
  }, {
    totalItemsQuantity: 0,
    itemImages: []
  });
  const moreDetailsToggle = /*#__PURE__*/React__default["default"].createElement("div", {
    className: ShipmentRow_module["default"].moreDetailsContainer
  }, /*#__PURE__*/React__default["default"].createElement(TextLink__default["default"], {
    linkStyle: {
      sDefault: linksNoneDefault,
      sHover: linksNoneHover
    },
    onClick: () => setShowItemDetails(!showItemDetails),
    "aria-expanded": showItemDetails,
    "aria-controls": `shipment-item-details-${shipment.shipmentId ?? ''}`
  }, showItemDetails ? packShipmentsHideDetails : packShipmentsMoreDetails));
  const itemChunks = items.reduce((allItemChunks, _, index, arr) => {
    if (index % 2 === 0) {
      const [itemChunk0, itemChunk1] = arr.slice(index, index + 2);
      if (itemChunk0) {
        allItemChunks.push([itemChunk0, itemChunk1]);
      }
    }
    return allItemChunks;
  }, []);
  const itemTiles = /*#__PURE__*/React__default["default"].createElement("div", {
    className: ShipmentRow_module["default"].itemDetailsContainer,
    id: `shipment-item-details-${shipment?.shipmentId ?? ''}`,
    key: `shipment-item-tiles-${shipment?.shipmentId ?? ''}`
  }, (Array.isArray(itemChunks) ? itemChunks : []).map(([itemChunk0, itemChunk1]) => /*#__PURE__*/React__default["default"].createElement("div", {
    className: ShipmentRow_module["default"].shipmentItemContainer,
    key: `${itemChunk0?.productSku ?? ''} ${itemChunk1?.productSku ?? ''}`
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: ShipmentRow_module["default"].shipmentItem
  }, /*#__PURE__*/React__default["default"].createElement(nthPackItemsInThisShipment.ItemTile, {
    itemInfo: itemChunk0,
    itemTextTemplates: itemTextTemplates,
    featureName: featureName
  })), itemChunk1 && /*#__PURE__*/React__default["default"].createElement("div", {
    className: ShipmentRow_module["default"].shipmentItem
  }, /*#__PURE__*/React__default["default"].createElement(nthPackItemsInThisShipment.ItemTile, {
    itemInfo: itemChunk1,
    itemTextTemplates: itemTextTemplates,
    featureName: featureName
  })))));
  const shipmentContainerStyles = clsx.clsx({
    [ShipmentRow_module["default"].shipmentContainer]: !landscape,
    [ShipmentRow_module["default"].shipmentContainerLandscape]: landscape
  });
  const shipmentStatusStyles = clsx.clsx({
    [ShipmentRow_module["default"].shipmentStatus]: true,
    [ShipmentRow_module["default"].shipmentStatusLandscape]: landscape,
    [ShipmentRow_module["default"].shipmentStatusEddPresent]: !!(month && dayOfWeek && dayOfMonth)
  });
  const itemsInShipmentStyles = clsx.clsx({
    [ShipmentRow_module["default"].itemsInShipment]: true,
    [ShipmentRow_module["default"].itemsInShipmentLandscape]: landscape
  });
  const buttonContainerStyles = clsx.clsx({
    [ShipmentRow_module["default"].buttonContainer]: true,
    [ShipmentRow_module["default"].buttonContainerLandscape]: landscape
  });
  const isGiftExperience = giftSharingEnabled && category === layoutTree.CategoryConditionCategory.SHARING;
  const showItemImagesLabel = totalItemsQuantity > 1 && !isGiftExperience;
  const showItemImages = itemImages.length > 0 && !isGiftExperience;
  const isReturnsInitiated = trackingType === 'RETURNS' && status === 'JUSTSHIPPED';
  const isCurrentShipment = shipmentId === mainShipmentId;
  return /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, /*#__PURE__*/React__default["default"].createElement("div", {
    className: shipmentContainerStyles,
    style: {
      borderTopColor,
      borderTopStyle,
      borderTopWidth
    },
    "data-testid": "shipment-root"
  }, /*#__PURE__*/React__default["default"].createElement("div", {
    className: shipmentStatusStyles
  }, /*#__PURE__*/React__default["default"].createElement(nthBitHeader.MediumHeader, {
    as: "p",
    featureName: featureName,
    containerCustomStyle: {
      wordBreak: 'break-word',
      marginBottom: landscape && !(month && dayOfWeek && dayOfMonth) ? undefined : '1rem',
      lineHeight: 1
    }
  }, status && /*#__PURE__*/React__default["default"].createElement(TrackingStatusText["default"], {
    translations: {
      status: status || undefined,
      category: statusCode || undefined
    }
  })), formattedOrderedDate[0] && formattedOrderedDate[1] && formattedOrderedDate[2] && /*#__PURE__*/React__default["default"].createElement(nthBitHeader.SemibigHeader, {
    as: "p",
    featureName: featureName,
    containerCustomStyle: {
      wordBreak: 'break-word',
      marginBottom: landscape ? undefined : '1rem',
      lineHeight: 1
    }
  }, `${formattedOrderedDate[0]}, ${formattedOrderedDate[1]} ${formattedOrderedDate[2]}`)), /*#__PURE__*/React__default["default"].createElement("div", {
    className: itemsInShipmentStyles
  }, showItemImagesLabel && /*#__PURE__*/React__default["default"].createElement(Paragraph__default["default"], {
    id: `nth-pack-shipments-image-list-${index}`,
    featureName: featureName,
    containerCustomStyle: paragraphStyles
  }, mustache__default["default"].render(shipmentsItemLabel || '', {
    itemsCount: totalItemsQuantity,
    shipmentNumber: shipment.trackingNumber
  }, undefined, renderOptions)), showItemImages && /*#__PURE__*/React__default["default"].createElement("ul", {
    className: ShipmentRow_module["default"].imagesContainer,
    "data-testid": "images-container",
    "aria-labelledby": showItemImagesLabel ? `nth-pack-shipments-image-list-${index}` : undefined,
    "aria-label": !showItemImagesLabel ? mustache__default["default"].render(shipmentsItemLabel || '', {
      itemsCount: totalItemsQuantity,
      shipmentNumber: shipment.trackingNumber
    }, undefined, renderOptions) : undefined
  }, itemImages), !landscape && showItemDetails && itemTiles, !isGiftExperience && moreDetailsToggle), link && !isReturnsInitiated && !isCurrentShipment && /*#__PURE__*/React__default["default"].createElement("div", {
    className: buttonContainerStyles,
    "data-testid": "shipment-button-container"
  }, /*#__PURE__*/React__default["default"].createElement(Button__default["default"], {
    big: !landscape,
    featureName: featureName,
    onClick: () => {
      logEvent({
        clickCategory: nthHookContextAnalytics.NarvarClickSubTypes.NAVIGATION_ELEMENT,
        clickLinkDestinationUrl: link,
        clickLinkLabel: shipmentsButton,
        clickSubType: nthHookContextAnalytics.NarvarClickSubTypes.SHIPMENTS,
        clickType: nthHookContextAnalytics.NarvarClickTypes.INTERACTION,
        eventComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_PACK_SHIPMENTS,
        eventFeature: nthHookContextAnalytics.NarvarEventComponents.NTH_PACK_SHIPMENTS,
        eventType: nthHookContextAnalytics.NarvarEventTypes.CLICK,
        feature: featureName,
        pageComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_PACK_SHIPMENTS
      });
      updatePageMainShipment();
    }
  }, shipmentsButton))), landscape && showItemDetails && itemTiles);
};
var ShipmentRow$1 = nthHookGlobalSettings.withGlobalSettingsProvider(nthHookSettings.withSettingsProvider(nthHookTranslations.withTranslationsProvider(nthHookTheme.withThemeProvider(ShipmentRow, {
  themeTokens: ['panelcontainerNoneNone', 'linksNoneDefault', 'linksNoneHover', 'headersSmallNone']
}), {
  translationKeys: ['shipmentsButton', 'shipmentsItemLabel', 'packShipmentsDateTokenOrder', 'packShipmentsMoreDetails', 'packShipmentsHideDetails', 'itemsInShipmentItemTitle', 'itemsInShipmentAttribute0', 'itemsInShipmentAttribute1', 'itemsInShipmentAttribute2', 'itemsInShipmentAttribute3']
}), {
  settingsRequested: `
        shipments {
          imageUrl
          url
        }
      `
}), {
  globalSettingsRequested: `
      giftSharingEnabled
      internationalDateFormatting
    `
});

exports["default"] = ShipmentRow$1;
